import {isFunction} from 'shared/utils/custom-utils';
import type {Identified} from 'typings/portaro.be.types';
import type {Ordered} from 'typings/portaro.be.types';

export function ascendingOrderComparator<T extends Ordered>(item1: T, item2: T): number {
    return item1.order - item2.order;
}

export function descendingOrderComparator<T extends Ordered>(item1: T, item2: T): number {
    return item2.order - item1.order;
}

export function compositeComparator<T>(...comparators: ((a: T, b: T) => number)[]): (a: T, b: T) => number {
    return (a, b) => {
        for (const comparator of comparators) {
            const result = comparator(a, b);
            if (result !== 0) {
                return result;
            }
        }
        return 0;
    }
}

/**
 * @module array-utils.fp
 * @name getFirstBy
 *
 * @template T
 *
 * @param {function(T): boolean} acceptFunction Function with parameter array item
 * @param {T=} defaultValue
 *
 * @returns {function(array: T[]): T}
 * @throws {Error} If `acceptFunction` is not function, throws error
 * @throws {Error} If `this` is not array, throws error
 *
 * @description
 * Accept function is predicate, which if it is true, `getFirstBy` return value, which made predicate true.
 * If predicate doesn't pass for any value and `defaultValue` is defined, then returns `defaultValue` else returns `null`.
 */
export function getFirstBy<T>(acceptFunction: (item: T) => boolean, defaultValue?: T): (array: T[]) => T {
    if(!isFunction(acceptFunction)) {
        throw new TypeError(`Invalid argument type. Function required, got: ${acceptFunction}`);
    }

    return (array: T[]): T => {
        Array.isArray(array);

        for (const element of array) {
            if (acceptFunction(element)) {
                return element;
            }
        }
        if (defaultValue) {
            return defaultValue;
        }
        return null;
    };
}

/**
 * @module array-utils.fp
 * @name getFirstByProperty
 *
 * Function returns _FIRST_ item, which its property equals to `value`.
 * If no value is filtered, return `defaultItem` if is specified or `null`.
 *
 * @template T
 *
 * @param {string} property Property name
 * @param {*} value Testing property value
 * @param {T=} defaultValue Default value
 * @param {boolean=} weakTypeCompare If comparing should be type strict or not
 *
 * @return {function(T[]): T}
 */
export function getFirstByProperty<T, K extends keyof T>(property: K, value: T[K], defaultValue?: T, weakTypeCompare: boolean = false): (array: T[]) => T {
    return getFirstBy((item) => {
        // FIXME: Possible problem == vs === in typescript
        // eslint-disable-next-line
        return weakTypeCompare ? item[property] == value : item[property] === value;
    }, defaultValue);
}

/**
 * Function returns _FIRST_ item, which property `id` equals to `idValue`.
 * If no value is filtered, return `defaultItem` if is specified or `null`.
 *
 * @template T
 *
 * @param {*} idValue Testing `idValue` value
 * @param {T=} defaultValue Default value
 * @param {boolean=} weakTypeCompare If comparing should be type strict or not
 *
 * @return {function(T[]): T}
 */
export function getFirstById<T extends Identified<K>, K>(idValue: K, defaultValue?: T, weakTypeCompare: boolean = false): (array: T[]) => T {
    return getFirstByProperty('id', idValue, defaultValue, weakTypeCompare);
}

/**
 * Replace all items in array, which have property `property` equal to `replacement` `property` by `replacement`.
 *
 * @template T
 *
 * @param {string=} property Property name
 * @param {T} replacement Replacement object
 *
 * @return {function(T[]): T[]}
 */
export function replaceAllByProperty<T, K extends keyof T>(property: K, replacement: T): (array: T[]) => T[] {
    return (array: T[]): T[] => {
        Array.isArray(array);

        // other forms of iteration do not allow for array elements modification (i think)
        for (let i = 0; i < array.length; i++) {
            if (array[i][property] === replacement[property]) {
                array[i] = replacement;
            }
        }
        return array;
    };
}

/**
 * Replace all items in array, which have property `id` equal to `replacement` `id` by `replacement`.
 *
 * @template T
 *
 * @param {T} replacement Replacement object
 *
 * @return {function(T[]): T[]}
 */
export function replaceAllById<T extends U, U extends Identified<any>>(replacement: T): (array: U[]) => U[] {
    return replaceAllByProperty('id', replacement);
}

/**
 * @module array-utils.fp
 * @name remove
 * @kind function
 *
 * @template T
 *
 * @param {any} item Item for comparision
 * @param {function(T, number): boolean=} equalsFn Comparator function. By default is `(currentElement) => currentElement === item`
 * @param {T} equalsFn.currentElement Current element.
 * @param {number} equalsFn.index Index
 *
 * @returns {function(array: T[]): T[]}
 *
 * @describe
 * Function removes all items in array which equals with item passed in first argument.
 * _Function mutates source array_
 */
export function remove<T>(item: any, equalsFn: (currentElement: T, index?: number) => boolean = (currentElement) => currentElement === item ) {
    if(!isFunction(equalsFn)) {
        throw new TypeError(`Invalid argument type. Function required, got: ${equalsFn}`);
    }

    return (array: T[]): T[] => {
        Array.isArray(array);

        for (let i = 0; i < array.length; i++) {
            if (equalsFn(array[i], i)) {
                array.splice(i, 1);
                i--;
            }
        }
        return array;
    };
}

/**
 * @module array-utils.fp
 * @name removeByProperty
 * @kind function
 *
 * @template T
 *
 * @param {T} propertyValue
 * @param {string} propertyName
 *
 * @return {function(array: T[]): T[]}
 *
 * @description
 * Removes all array elements, where its property (which is described by `propertyName`) equals to `propertyValue`.
 * _Function mutates source array_
 *
 * @example
 * ```js
 *  const arr = [{id: 1, val: '1'}, {id: 2, val: '2'}, {id: 3, val: '3'}, {id: 4, val: '4'}, {id: 5, val: '5'}];
 *  const result = removeByProperty(3, 'id')(arr);
 *  // result = [{id: 1, val: '1'}, {id: 2, val: '2'}, {id: 4, val: '4'}, {id: 5, val: '5'}];
 * ```
 */
export function removeByProperty<T>(propertyValue: any, propertyName: string): (array: T[]) => T[] {
    return remove(propertyValue, (element) => {
        return element[propertyName] === propertyValue;
    });
}

/**
 * @module array-utils.fp
 * @name inline
 *
 * @param {string=} delimiter Default is `,`
 * @param {string=} prefix Prefix, default ''
 * @param {string=} suffix Postfix, default ''
 *
 * @returns {function(array: Array): string}
 *
 * @description
 * Function joins array items with `delimiter`, adds `prefix` and `postfix` to each element
 */
export function inline(delimiter: string = ',', prefix: string = '', suffix: string = '') {
    return (array: any[]) => {
        Array.isArray(array);

        let res = '';
        for (const element of array) {
            if (res.length > 0) {
                res += delimiter;
            }
            res += `${prefix}${element}${suffix}`;
        }
        return res;
    };
}

/**
 * @module array-utils.fp
 * @name range
 *
 * @param {number} start first number in sequence
 * @param {number} stop last number in sequence
 * @param {number=} step difference between numbers in sequence
 * @returns {number[]}
 *
 * @description
 * Generates a sequence of numbers as array, defined by (inclusive) number range
 */
export function range(start: number, stop: number, step = 1): number[] {
    if (step > 0 && stop < start) {
        throw new Error(`For step of ${step} start value should be less or equal to stop value`);
    }
    if (step < 0 && stop > start) {
        throw new Error(`For step of ${step} start value should be more or equal to stop value`);
    }
    return Array.from({ length: (stop - start) / step + 1}, (_, i) => start + (i * step));
}


/**
 * @module array-utils.fp
 * @name includesAllFrom
 *
 * @template T
 * @param {T[]} testedArray array which we test if it contains all values from includedValues
 * @param {T[]} includedValues values that should be included inside testedArray
 * @returns {boolean}
 *
 * @description
 * Test whether all includedValues are present in testedArray
 */
export function includesAllFrom<T>(testedArray: T[], includedValues: T[]): boolean {
    Array.isArray(testedArray);
    Array.isArray(includedValues);

    return includedValues.every((element) => testedArray.includes(element));
}


/**
 * @module array-utils.fp
 * @name zip
 *
 * @template U
 * @template V
 * @param {U[]} array1 first input array
 * @param {V[]} array2 second input array of SAME LENGTH as the first array
 * @returns {[U, V][]}  array of tuples created from elements from both input arrays
 *
 * @description
 * Creates new array by combining elements with same index from input arrays into tuples.
 * Input arrays need to have SAME LENGTH.
 */
export function zip<U, V>(array1: U[], array2:V[]): [U, V][] {
    Array.isArray(array1);
    Array.isArray(array2);

    if (array1.length !== array2.length) {
        throw new Error('ZIP: arrays are not same length');
    }

    return array1.map((value1,index) => [value1, array2[index]]);
}


export function assertIsNonEmptyArray<T>(array: T[]): asserts array is [T, ...T[]] {
    if (!Array.isArray(array) || array.length === 0) {
        throw new Error('Argument is not an array or it is empty');
    }
}
